const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

// Google Places (New) structured facts, used as a COMPLEMENT to owned data:
// fetched at view time for the places actually on screen, cached for the
// ToS-permitted 30 days, always attributed, and skipped for any attribute
// our own verified/community data already answers. Never used to build or
// train our dataset — that stays on sources we own.
const MEM = new Map();
const LS_PREFIX = 'g-facts-v3:'; // v3: distance-gated matching (kills wrong-place photos)
const CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days (Google ToS ceiling)
const MISS_TTL = 7 * 24 * 60 * 60 * 1000;

// Text search returns Google's best *name* match anywhere — the location bias
// is only a bias, not a filter — so a cryptic name like "E-27" can match a
// Vermont condo 135 miles away and we'd show its photo. We reject any match
// too far from the POI's real coordinates. Area types (parks, preserves) get
// more slack because their OSM centroid and Google's pin diverge; point
// features must be close.
const AREA_TYPES = new Set(['park', 'preserve', 'reservoir', 'water', 'hike', 'marina']);
const MATCH_RADIUS_MI = { area: 2, point: 0.6 };

// Names too cryptic to text-search reliably (e.g. "E-27", "W-24", "Lot 3"):
// skip Google entirely rather than risk a confident-wrong match.
function isCrypticName(name) {
  const n = String(name || '').trim();
  if (n.length < 4) return true;
  if (/^[a-z]?-?\d+[a-z]?$/i.test(n)) return true; // E-27, W24, 12b
  const letters = (n.match(/[a-z]/gi) || []).length;
  return letters < 3; // mostly punctuation/digits
}

function milesBetween(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3958.8;
  const a =
    Math.sin(toRad(lat2 - lat1) / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(toRad(lon2 - lon1) / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function readCache(poiId) {
  if (MEM.has(poiId)) return MEM.get(poiId);
  try {
    const raw = localStorage.getItem(LS_PREFIX + poiId);
    if (!raw) return undefined;
    const { at, data } = JSON.parse(raw);
    const ttl = data === null ? MISS_TTL : CACHE_TTL;
    if (Date.now() - at > ttl) {
      localStorage.removeItem(LS_PREFIX + poiId);
      return undefined;
    }
    MEM.set(poiId, data);
    return data;
  } catch {
    return undefined;
  }
}

function writeCache(poiId, data) {
  MEM.set(poiId, data);
  try {
    localStorage.setItem(LS_PREFIX + poiId, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* storage full — memory cache still applies */
  }
}

function parkingLabel(parking) {
  if (!parking) return null;
  if (parking.freeParkingLot) return 'Free parking lot';
  if (parking.freeGarageParking) return 'Free garage parking';
  if (parking.freeStreetParking) return 'Free street parking';
  if (parking.paidParkingLot || parking.paidGarageParking) return 'Paid parking';
  if (parking.paidStreetParking) return 'Paid street parking';
  if (parking.valetParking) return 'Valet parking';
  return null;
}

/**
 * Structured facts for one POI, keyed to our attribute schema where they
 * overlap (so callers can suppress Google's answer when a verified one
 * exists). Returns null when unavailable.
 */
export async function getPlaceFacts(poi) {
  if (!API_KEY || !poi?.name) return null;

  // Cryptic names (DEC access codes, lot numbers) can't be text-searched
  // reliably — showing nothing beats showing a wrong place's photo.
  if (isCrypticName(poi.name)) {
    writeCache(poi.id, null);
    return null;
  }

  const cached = readCache(poi.id);
  if (cached !== undefined) return cached;

  try {
    const response = await fetch('https://places.googleapis.com/v1/places:searchText', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': [
          'places.id',
          'places.displayName',
          'places.location',
          'places.rating',
          'places.userRatingCount',
          'places.photos',
          'places.googleMapsUri',
          'places.parkingOptions',
          'places.evChargeOptions',
          'places.accessibilityOptions',
          'places.goodForChildren',
          'places.allowsDogs',
          'places.restroom',
          'places.reviews',
        ].join(','),
      },
      body: JSON.stringify({
        textQuery: poi.name,
        locationBias: { circle: { center: { latitude: poi.lat, longitude: poi.lon }, radius: 1500 } },
        maxResultCount: 1,
      }),
    });

    if (!response.ok) {
      writeCache(poi.id, null);
      return null;
    }

    const place = (await response.json()).places?.[0];
    if (!place) {
      writeCache(poi.id, null);
      return null;
    }

    // Distance gate: reject a match that's too far from the real POI (the
    // location bias doesn't enforce proximity, so this is what prevents
    // Killington-condo photos on a Rondout fishing hole).
    if (poi.lat != null && place.location) {
      const dist = milesBetween(poi.lat, poi.lon, place.location.latitude, place.location.longitude);
      const limit = AREA_TYPES.has(poi.type) ? MATCH_RADIUS_MI.area : MATCH_RADIUS_MI.point;
      if (dist > limit) {
        writeCache(poi.id, null);
        return null;
      }
    }

    const photo = place.photos?.[0];
    const facts = {
      rating: place.rating ?? null,
      ratingCount: place.userRatingCount ?? null,
      googleMapsUri: place.googleMapsUri ?? null,
      photoUri: photo?.name
        ? `https://places.googleapis.com/v1/${photo.name}/media?maxWidthPx=640&key=${API_KEY}`
        : null,
      photoAttribution: photo?.authorAttributions?.[0]?.displayName ?? null,
      reviews: (place.reviews || []).slice(0, 3).map((r) => ({
        author: r.authorAttribution?.displayName || 'Anonymous',
        rating: r.rating,
        text: r.text?.text || '',
        relativeTime: r.relativePublishTimeDescription || '',
      })),
      // Keyed to our schema where they overlap, so verified answers win.
      byAttribute: {
        ...(place.allowsDogs != null && { dog_friendly: place.allowsDogs ? 'Dogs allowed' : 'No dogs' }),
        ...(place.goodForChildren && { kid_friendly: 'Good for kids' }),
        ...(place.restroom && { restrooms: 'Restrooms' }),
        ...(parkingLabel(place.parkingOptions) && { parking: parkingLabel(place.parkingOptions) }),
      },
      // No owned equivalent — always worth showing.
      extras: [
        ...(place.evChargeOptions?.connectorCount
          ? [`EV charging (${place.evChargeOptions.connectorCount} connectors)`]
          : place.evChargeOptions ? ['EV charging'] : []),
        ...(place.accessibilityOptions?.wheelchairAccessibleEntrance ? ['Wheelchair accessible'] : []),
      ],
    };

    writeCache(poi.id, facts);
    return facts;
  } catch {
    return null;
  }
}

/**
 * Back-compat adapter for POICard (old call signature and field names).
 */
export async function enrichPOIWithGoogleData(poiId, poiName, lat, lon) {
  const facts = await getPlaceFacts({ id: poiId, name: poiName, lat, lon });
  if (!facts) return null;
  return {
    rating: facts.rating,
    userRatingCount: facts.ratingCount,
    reviews: facts.reviews ?? [],
    googleMapsUri: facts.googleMapsUri,
    photoUri: facts.photoUri,
  };
}
